import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Form, Input, Button } from "antd";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";

import { AutoModel } from "../../components/common/AutoModel";
import { sendSmsCodeApi, resetPasswordApi } from "../../service/login";

import "./ForgotPasswordModal.scss";

/** 倒计时 60s（与后端 SmsCodeService 限流阈值一致） */
const COUNTDOWN_SECONDS = 60;

/** 业务场景：忘记密码 - 对应后端 SmsSceneEnum.MEMBER_RESET_PASSWORD = 4 */
const SCENE_FORGOT_PASSWORD = 4;

export interface ForgotPasswordModalProps {
  /** 是否展示 */
  open: boolean;
  /** 关闭弹窗（右上角 / 遮罩 / Esc） */
  onClose: () => void;
  /** 成功重置后回调，父组件可在里面做跳转或提示 */
  onSuccess?: () => void;
  /**
   * 全局提示：必须由渲染 <AlertMessage> 的父组件传入，
   * 否则弹窗内的 showSuccess 不会展示到页面上。
   */
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
}

interface FormValues {
  mobile?: string;
  code?: string;
  newPassword?: string;
  confirmPassword?: string;
}

/**
 * 忘记密码弹窗
 * 流程：手机号 → 短信验证码 → 新密码 → 确认密码 → 提交
 */
export const ForgotPasswordModal: FC<ForgotPasswordModalProps> = ({
  open,
  onClose,
  onSuccess,
  showSuccess,
  showError,
}) => {
  const [form] = Form.useForm<FormValues>();

  const [submitting, setSubmitting] = useState(false);
  const [sending, setSending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const countdownTimerRef = useRef<number | null>(null);

  // 清理倒计时
  useEffect(() => {
    return () => {
      if (countdownTimerRef.current) {
        window.clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };
  }, []);

  // 弹窗关闭时重置表单
  useEffect(() => {
    if (!open) {
      form.resetFields();
      setShowPassword(false);
      setShowConfirmPassword(false);
      if (countdownTimerRef.current) {
        window.clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
      setCountdown(0);
    }
  }, [open, form]);

  // 启动倒计时
  const startCountdown = useCallback(() => {
    setCountdown(COUNTDOWN_SECONDS);
    if (countdownTimerRef.current) {
      window.clearInterval(countdownTimerRef.current);
    }
    countdownTimerRef.current = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (countdownTimerRef.current) {
            window.clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  /** 点击「获取验证码」 */
  const onSendSmsCode = useCallback(async () => {
    if (sending || countdown > 0) return;
    // 关键：先从 DOM 同步到 Form，再校验，避免自动填充场景下 Form 内部值过时
    let domMobile = "";
    if (typeof document !== "undefined") {
      // antd Form.Item 不会把 name 透传到 input，所以用 getFieldInstance 或显式 id 找元素
      let el: HTMLInputElement | null = null;
      try {
        const inst = form.getFieldInstance?.("mobile");
        el =
          ((inst as any)?.input as HTMLInputElement) ||
          (inst as HTMLInputElement) ||
          null;
      } catch {
        /* noop */
      }
      if (!el) {
        el = document.getElementById(
          "auth-forgot-mobile",
        ) as HTMLInputElement | null;
      }
      if (el) {
        domMobile = el.value;
        if (domMobile !== form.getFieldValue("mobile")) {
          form.setFieldsValue({ mobile: domMobile });
        }
      }
    }
    let mobile = "";
    try {
      mobile = (await form.validateFields(["mobile"])).mobile || "";
    } catch {
      // 校验未通过，antd 已在字段上展示红字
      return;
    }
    // 自动填充场景下，DOM 有值但 Form 仍可能为空，给一次兜底
    if (!mobile && domMobile) {
      mobile = domMobile;
    }
    if (!/^1[3-9]\d{9}$/.test(mobile)) {
      showError("请输入正确的手机号");
      return;
    }

    setSending(true);
    try {
      const res = await sendSmsCodeApi({ mobile, scene: SCENE_FORGOT_PASSWORD });
      if (res && res.code === 0) {
        showSuccess("验证码已发送，请注意查收短信");
        startCountdown();
      } else {
        showError(res?.msg || "验证码发送失败，请稍后重试");
      }
    } catch (err: any) {
      showError(
        err?.response?.msg ||
          err?.msg ||
          err?.detail ||
          err?.message ||
          "验证码发送失败，请稍后重试"
      );
    } finally {
      setSending(false);
    }
  }, [sending, countdown, form, showError, showSuccess, startCountdown]);

  /** 提交时强制从 DOM 直读密码，避免密码管理器 autofill 后 Form 状态与 DOM 不一致 */
  const readPwdFromDom = useCallback((name: string): string => {
    if (typeof document === "undefined") return "";
    // antd Form.Item 不会把 name 透传到 input，所以用 getFieldInstance 或显式 id 找元素
    let el: HTMLInputElement | null = null;
    try {
      const inst = form.getFieldInstance?.(name);
      el =
        ((inst as any)?.input as HTMLInputElement) ||
        (inst as HTMLInputElement) ||
        null;
    } catch {
      /* noop */
    }
    if (!el) {
      const idMap: Record<string, string> = {
        mobile: "auth-forgot-mobile",
        code: "auth-forgot-code",
        newPassword: "auth-forgot-newPassword",
        confirmPassword: "auth-forgot-confirmPassword",
      };
      const id = idMap[name];
      if (id) {
        el = document.getElementById(id) as HTMLInputElement | null;
      }
    }
    return el ? el.value : "";
  }, []);

  /** 点击「提交」 */
  const onFinish = useCallback(
    async (values: FormValues) => {
      if (submitting) return;
      const mobile = values.mobile || readPwdFromDom("mobile");
      const code = values.code || readPwdFromDom("code");
      // 关键：密码字段以 DOM 实时值为准，autofill 不会触发 onChange，Form 内部值可能过时
      const newPassword = readPwdFromDom("newPassword") || values.newPassword || "";
      const confirmPassword = readPwdFromDom("confirmPassword") || values.confirmPassword || "";

      if (!/^1[3-9]\d{9}$/.test(mobile)) {
        showError("请输入正确的手机号");
        return;
      }
      if (!code.trim()) {
        showError("请输入短信验证码");
        return;
      }
      if (!newPassword || newPassword.length < 6) {
        showError("密码长度至少 6 个字符");
        return;
      }
      if (newPassword !== confirmPassword) {
        showError("两次输入的密码不一致");
        return;
      }

      setSubmitting(true);
      try {
        const res = await resetPasswordApi({ mobile, code, newPassword });
        if (res && res.code === 0) {
          showSuccess("密码重置成功，请使用新密码登录");
          // 延迟关闭弹窗与回调，确保用户能看到成功提示
          // 注意：必须先 showSuccess，再延后关闭，否则父组件立刻 unmount 会把消息一并销毁
          setTimeout(() => {
            onSuccess?.();
            onClose();
          }, 500);
        } else {
          showError(res?.msg || "密码重置失败，请稍后重试");
        }
      } catch (err: any) {
        showError(
          err?.response?.msg ||
            err?.msg ||
            err?.detail ||
            err?.message ||
            "密码重置失败，请稍后重试"
        );
      } finally {
        setSubmitting(false);
      }
    },
    [submitting, showError, showSuccess, onSuccess, onClose, readPwdFromDom]
  );

  return (
    <AutoModel
      open={open}
      onClose={submitting ? undefined : onClose}
      title="重置密码"
      size="default"
      maskClosable={!submitting}
      keyboard={!submitting}
      bodyClassName="forgot-password-modal__body"
      contentClassName="forgot-password-modal__panel"
    >
      <p className="forgot-password-modal__desc">请验证您的手机号以继续</p>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={onFinish}
        className="forgot-password-modal__form"
      >
        <Form.Item
          label="手机号"
          name="mobile"
          rules={[
            { required: true, message: "请输入手机号" },
            { pattern: /^1[3-9]\d{9}$/, message: "请输入正确的手机号格式" },
          ]}
        >
          <Input id="auth-forgot-mobile" placeholder="请输入手机号" disabled={submitting} maxLength={11} autoComplete="tel" />
        </Form.Item>        <div className="forgot-password-modal__field">
          <label className="forgot-password-modal__label" htmlFor="auth-forgot-code">验证码</label>
          <div className="forgot-password-modal__sms-row">
            <Form.Item
              name="code"
              rules={[
                { required: true, message: "请输入短信验证码" },
              ]}
            >
              <Input
                id="auth-forgot-code"
                placeholder="请输入验证码"
                disabled={submitting}
                maxLength={8}
                autoComplete="one-time-code"
              />
            </Form.Item>
            <button
              type="button"
              className="forgot-password-modal__send-btn"
              onClick={onSendSmsCode}
              disabled={sending || countdown > 0 || submitting}
            >
              {countdown > 0 ? `${countdown}s 后重试` : sending ? "发送中..." : "获取验证码"}
            </button>
          </div>
        </div>

        <Form.Item
          label="新密码"
          name="newPassword"
          rules={[
            { required: true, message: "请输入新密码" },
            { min: 6, message: "密码至少 6 个字符" },
            { max: 20, message: "密码最多 20 个字符" },
          ]}
        >
          <div className="forgot-password-modal__pwd-wrap">
            <Input
              id="auth-forgot-newPassword"
              type={showPassword ? "text" : "password"}
              placeholder="请输入新密码（至少6位）"
              disabled={submitting}
              autoComplete="new-password"
              maxLength={20}
              style={{ paddingRight: "3rem" }}
            />
            <button
              type="button"
              className="forgot-password-modal__pwd-toggle"
              aria-label={showPassword ? "隐藏密码" : "显示密码"}
              onClick={() => setShowPassword((v) => !v)}
            >
              {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            </button>
          </div>
        </Form.Item>

        <Form.Item
          label="确认密码"
          name="confirmPassword"
          dependencies={["newPassword"]}
          rules={[
            { required: true, message: "请再次输入新密码" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("newPassword") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error("两次输入的密码不一致"));
              },
            }),
          ]}
        >
          <div className="forgot-password-modal__pwd-wrap">
            <Input
              id="auth-forgot-confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="请再次输入新密码"
              disabled={submitting}
              autoComplete="new-password"
              maxLength={20}
              style={{ paddingRight: "3rem" }}
            />
            <button
              type="button"
              className="forgot-password-modal__pwd-toggle"
              aria-label={showConfirmPassword ? "隐藏密码" : "显示密码"}
              onClick={() => setShowConfirmPassword((v) => !v)}
            >
              {showConfirmPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            </button>
          </div>
        </Form.Item>        <Form.Item className="forgot-password-modal__actions">
          <Button
            type="primary"
            htmlType="submit"
            className="forgot-password-modal__submit"
            loading={submitting}
            disabled={submitting}
          >
            {submitting ? "提交中..." : "重置密码"}
          </Button>
        </Form.Item>
        <div className="forgot-password-modal__bottom-row">
          <button type="button" onClick={onClose} disabled={submitting}>返回登录</button>
        </div>
      </Form>
    </AutoModel>
  );
};
